// `scriptctl ingest status` — derives ingest progress from the workspace's REAL
// file state (§5.3), never from a separate state-machine file. The pipeline
// already treats the workspace as its source of truth (resume = "what's on
// disk"), so status is a pure function of the files: no run_state.json to drift
// out of sync. This is the one authoritative status derivation — the runtime
// watcher and frontend consume this via `--json` instead of re-deriving it.

import * as fs from "node:fs";
import * as path from "node:path";

import { EXIT_OK, type CommandOutput } from "../../common.js";
import {
  createOperationalFailure,
  type FailureType,
} from "../../failures.js";
import type { ScriptV4 } from "../../domain/script/v4/types.js";
import {
  parseIngestErrorSidecar,
  type IngestErrorSidecar,
  type IngestStatusError,
} from "./failure.js";
import { summarizeTimings, type IngestTimings, type TimingSummary } from "./timings.js";
import type { VideoSkipRecord } from "./video-skip-state.js";

type Dict = Record<string, unknown>;
const strOf = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

export type IngestKind = "text" | "video";
export type IngestState = "empty" | "incomplete" | "failed" | "ready";

export interface IngestUnit {
  total: number;
  done: number;
  failed: number;
}

export interface IngestPass {
  name: string;
  done: boolean;
  unit?: IngestUnit;
}

export interface IngestFailureSummary {
  total: number;
  retryable: number;
  terminal: number;
  by_type: Partial<Record<FailureType, number>>;
}

export interface IngestSkipSummary {
  episode: number;
  skipped_at: string;
  reason: string;
  failure_type: FailureType;
  failure_code: string;
}

export interface IngestStatus {
  workspace: string;
  exists: boolean;
  kind: IngestKind | null;
  state: IngestState;
  phase: string;
  passes: IngestPass[];
  units: IngestUnit | null; // primary units (chunks / episodes) for a coarse bar
  script: boolean;
  validation: { ok: boolean; errors: number; warnings: number } | null;
  failure_summary: IngestFailureSummary;
  errors: IngestStatusError[];
  updated: string | null;
  // Real wall-clock timing of the current/last run (elapsed, per-phase, serial ETA);
  // null before the first run writes timings.json.
  timings: TimingSummary | null;
  completed?: number;
  remaining?: number;
  complete?: boolean;
  next_episode?: number | null;
  parsed?: number;
  skipped?: number;
  skipped_episodes?: number[];
  has_skips?: boolean;
  skips?: IngestSkipSummary[];
}

function readTimings(root: string): TimingSummary | null {
  try {
    const t = JSON.parse(fs.readFileSync(path.join(root, "timings.json"), "utf-8")) as IngestTimings;
    return summarizeTimings(t, Date.now());
  } catch {
    return null;
  }
}

interface Manifest {
  updated?: string;
  source?: { kind?: string };
  chunks?: number;
  episodes?: unknown[];
  current_error?: IngestErrorSidecar;
  skips?: VideoSkipRecord[];
}

// List files directly under a pass dir matching a suffix (non-recursive).
function listSuffix(root: string, dir: string, suffix: string): string[] {
  const abs = path.join(root, dir);
  let names: string[];
  try {
    names = fs.readdirSync(abs);
  } catch {
    return [];
  }
  return names.filter((n) => n.endsWith(suffix)).sort();
}

const countSuffix = (root: string, dir: string, suffix: string): number => listSuffix(root, dir, suffix).length;
const has = (root: string, rel: string): boolean => fs.existsSync(path.join(root, rel));

function readManifest(root: string): Manifest | null {
  try {
    return JSON.parse(fs.readFileSync(path.join(root, "manifest.json"), "utf-8")) as Manifest;
  } catch {
    return null;
  }
}

function readValidation(root: string): IngestStatus["validation"] {
  try {
    const v = JSON.parse(fs.readFileSync(path.join(root, "validation.json"), "utf-8")) as {
      ok?: boolean;
      errors?: unknown[];
      warnings?: unknown[];
    };
    return { ok: Boolean(v.ok), errors: v.errors?.length ?? 0, warnings: v.warnings?.length ?? 0 };
  } catch {
    return null;
  }
}

function legacyError(pass: string, unit: string, message: string): IngestStatusError {
  // Text pass2 sidecars are checkpointed normalization-unit failures whose
  // established recovery contract is to rerun that unit. Old video sidecars do
  // not carry enough information to determine retryability without guessing.
  const retryable = pass === "pass2";
  return {
    pass,
    unit,
    phase: pass,
    attempts: [],
    ...createOperationalFailure(
      { failure_type: "unknown", failure_code: "legacy_unclassified" },
      { retryable, message },
    ),
  };
}

// Collect the error sidecars a failed run leaves behind (cleared on unit success).
function readError(root: string, pass: string, unit: string): IngestStatusError {
  try {
    const value: unknown = JSON.parse(fs.readFileSync(path.join(root, pass, `${unit}.error.json`), "utf-8"));
    const sidecar = parseIngestErrorSidecar(value);
    if (sidecar) {
      return {
        pass,
        unit,
        phase: sidecar.phase,
        attempts: sidecar.attempts,
        ...sidecar.failure,
      };
    }
    if (typeof value === "object" && value !== null && "error" in value && typeof value.error === "string") {
      return legacyError(pass, unit, value.error);
    }
    return legacyError(pass, unit, "Error sidecar has an unsupported schema.");
  } catch {
    return legacyError(pass, unit, "Error sidecar is unreadable.");
  }
}

function collectErrors(root: string, pass: string, ignoredUnits: ReadonlySet<string> = new Set()): IngestStatusError[] {
  return listSuffix(root, pass, ".error.json")
    .map((name) => name.replace(/\.error\.json$/, ""))
    .filter((unit) => !ignoredUnits.has(unit))
    .map((unit) => readError(root, pass, unit));
}

function currentVideoError(
  manifest: Manifest,
  acceptedEpisodes: number,
  ignoredUnits: ReadonlySet<string>,
): IngestStatusError[] {
  const sidecar = parseIngestErrorSidecar(manifest.current_error);
  if (!sidecar) return [];
  const unit = `ep_${String(sidecar.unit.episode).padStart(3, "0")}`;
  if (sidecar.unit.episode <= acceptedEpisodes || ignoredUnits.has(unit)) return [];
  return [{
    pass: "ep",
    unit,
    phase: sidecar.phase,
    attempts: sidecar.attempts,
    ...sidecar.failure,
  }];
}

function summarizeFailures(errors: IngestStatusError[]): IngestFailureSummary {
  const by_type: Partial<Record<FailureType, number>> = {};
  for (const error of errors) by_type[error.failure_type] = (by_type[error.failure_type] ?? 0) + 1;
  const retryable = errors.filter((error) => error.retryable).length;
  return { total: errors.length, retryable, terminal: errors.length - retryable, by_type };
}

// Episode count for the serial pass bar; derived from the assembled body.
function readEpisodeCount(root: string): number {
  try {
    const s = JSON.parse(fs.readFileSync(path.join(root, "body.json"), "utf-8")) as { episodes?: unknown[] };
    return Array.isArray(s.episodes) ? s.episodes.length : 0;
  } catch {
    return 0;
  }
}

function readFinalizedVideoEpisodeCount(root: string): number | null {
  try {
    const script = JSON.parse(fs.readFileSync(path.join(root, "script.json"), "utf-8")) as ScriptV4;
    return script.version === 4 && Array.isArray(script.episodes) ? script.episodes.length : null;
  } catch {
    return null;
  }
}

function readAcceptedVideoEpisodeCount(root: string, total: number): number {
  try {
    const context = JSON.parse(fs.readFileSync(path.join(root, "context.json"), "utf-8")) as { lastEpisode?: unknown };
    return typeof context.lastEpisode === "number" && Number.isInteger(context.lastEpisode)
      ? Math.min(Math.max(context.lastEpisode, 0), total)
      : 0;
  } catch {
    return 0;
  }
}

function textPasses(root: string, manifest: Manifest): { passes: IngestPass[]; units: IngestUnit } {
  const total = manifest.chunks ?? 0;
  const normalize: IngestUnit = {
    total,
    done: listSuffix(root, "pass2", ".json").filter((name) => /^chunk_\d+\.json$/.test(name)).length,
    failed: countSuffix(root, "pass2", ".error.json"),
  };
  // Serial govern: one `serial/win_<start>_<end>.json` per finished window (a window
  // covers 1+ contiguous episodes). Progress = episodes covered = sum of ranges.
  const epTotal = readEpisodeCount(root);
  const serialDone = listSuffix(root, "serial", ".json").reduce((n, name) => {
    const m = name.match(/^win_(\d+)_(\d+)\.json$/);
    return m ? n + (Number(m[2]) - Number(m[1]) + 1) : n;
  }, 0);
  const serial: IngestUnit = { total: epTotal, done: serialDone, failed: 0 };
  const passes: IngestPass[] = [
    { name: "normalize", done: total > 0 && normalize.done >= total && normalize.failed === 0, unit: normalize },
    { name: "assemble", done: has(root, "body.json") },
    { name: "serial", done: epTotal > 0 && serialDone >= epTotal, unit: serial },
    { name: "govern", done: has(root, "script.json") },
  ];
  return { passes, units: normalize };
}

function videoPasses(
  root: string,
  manifest: Manifest,
  skips: readonly VideoSkipRecord[],
  acceptedPrefix: number,
  failedEpisodes: number,
): { passes: IngestPass[]; units: IngestUnit; parsed: number } {
  const total = manifest.episodes?.length ?? 0;
  const parsed = acceptedPrefix - skips.filter((record) => record.episode <= acceptedPrefix).length;
  // context.json is the authoritative acceptance checkpoint. An episode JSON
  // written immediately before a crash is retried unless context includes it.
  const serial: IngestUnit = {
    total,
    done: acceptedPrefix,
    failed: failedEpisodes,
  };
  const passes: IngestPass[] = [
    { name: "serial", done: total > 0 && serial.done >= total && serial.failed === 0, unit: serial },
    { name: "finalize", done: acceptedPrefix >= total && readFinalizedVideoEpisodeCount(root) === acceptedPrefix },
  ];
  return { passes, units: serial, parsed };
}

export function deriveIngestStatus(workspacePath: string): IngestStatus {
  const root = path.resolve(workspacePath);
  const manifest = readManifest(root);
  if (!manifest) {
    return {
      workspace: root, exists: false, kind: null, state: "empty", phase: "none",
      passes: [], units: null, script: false, validation: null,
      failure_summary: summarizeFailures([]), errors: [], updated: null, timings: null,
    };
  }
  const kind: IngestKind = manifest.source?.kind === "text" ? "text" : "video";
  const videoTotal = manifest.episodes?.length ?? 0;
  const acceptedVideoEpisodes = kind === "video" ? readAcceptedVideoEpisodeCount(root, videoTotal) : 0;
  const videoIncomplete = kind === "video" && acceptedVideoEpisodes < videoTotal;
  const skipRecords = kind === "video" ? manifest.skips ?? [] : [];
  const skippedUnits = new Set(skipRecords.map((record) => `ep_${String(record.episode).padStart(3, "0")}`));
  const videoErrors = kind === "video" && acceptedVideoEpisodes < videoTotal
    ? currentVideoError(manifest, acceptedVideoEpisodes, skippedUnits)
    : [];
  const video = kind === "video"
    ? videoPasses(root, manifest, skipRecords, acceptedVideoEpisodes, videoErrors.length)
    : null;
  const { passes, units } = kind === "text" ? textPasses(root, manifest) : video!;

  const errors = kind === "text" ? collectErrors(root, "pass2") : videoErrors;

  if (videoIncomplete) {
    const acceptedSkips = skipRecords.filter((record) => record.episode <= acceptedVideoEpisodes);
    return {
      workspace: root,
      exists: true,
      kind,
      state: errors.length > 0 ? "failed" : "incomplete",
      phase: "serial",
      passes,
      units,
      script: acceptedVideoEpisodes > 0,
      validation: null,
      failure_summary: summarizeFailures(errors),
      errors,
      updated: manifest.updated ?? null,
      timings: null,
      completed: acceptedVideoEpisodes,
      remaining: videoTotal - acceptedVideoEpisodes,
      complete: false,
      next_episode: acceptedVideoEpisodes + 1,
      parsed: video?.parsed ?? acceptedVideoEpisodes,
      skipped: acceptedSkips.length,
      skipped_episodes: skipRecords.map((record) => record.episode),
      has_skips: skipRecords.length > 0,
      skips: skipRecords.map((record) => ({
        episode: record.episode,
        skipped_at: record.skipped_at,
        reason: record.reason,
        failure_type: record.failure.failure_type,
        failure_code: record.failure.failure_code,
      })),
    };
  }

  const script = has(root, "script.json");
  const validation = readValidation(root);
  if (validation && !validation.ok) {
    errors.push({
      pass: "finalize",
      unit: "script",
      phase: "validation",
      attempts: [],
      ...createOperationalFailure(
        { failure_type: "validation", failure_code: "script_validation_failed" },
        { retryable: false, message: `Script validation failed with ${validation.errors} error(s).` },
      ),
    });
  }

  // State is a pure fold over the files: a final valid script is `ready`; a
  // hard error (validation failed OR leftover unit error sidecars) is `failed`;
  // otherwise the run started but didn't finish (`incomplete`). Liveness (is a
  // process still running?) is deliberately NOT here — it can't be read from
  // files; that's the watcher's / agent's concern.
  let state: IngestState;
  const allPassesDone = passes.length > 0 && passes.every((pass) => pass.done);
  if ((kind === "text" || allPassesDone) && script && validation?.ok) state = "ready";
  else if (errors.length > 0 || (validation && !validation.ok)) state = "failed";
  else state = "incomplete";

  const firstPending = passes.find((p) => !p.done);
  const phase = state === "ready" ? "done" : firstPending ? firstPending.name : "done";

  const videoProgress = kind === "video"
    ? {
        completed: units.done,
        remaining: Math.max(0, units.total - units.done),
        complete: units.total > 0 && units.done >= units.total && units.failed === 0,
        next_episode: units.done >= units.total ? null : units.done + 1,
        parsed: video?.parsed ?? 0,
        skipped: skipRecords.filter((record) => record.episode <= units.done).length,
        skipped_episodes: skipRecords.map((record) => record.episode),
        has_skips: skipRecords.length > 0,
        skips: skipRecords.map((record) => ({
          episode: record.episode,
          skipped_at: record.skipped_at,
          reason: record.reason,
          failure_type: record.failure.failure_type,
          failure_code: record.failure.failure_code,
        })),
      }
    : {};

  return {
    workspace: root, exists: true, kind, state, phase, passes, units,
    script, validation, failure_summary: summarizeFailures(errors), errors,
    updated: manifest.updated ?? null, timings: readTimings(root),
    ...videoProgress,
  };
}

function unitStr(u: IngestUnit): string {
  const base = `${u.done}/${u.total}`;
  return u.failed > 0 ? `${base} (${u.failed} failed)` : base;
}

function humanLines(s: IngestStatus): string[] {
  if (!s.exists) return [`no ingest workspace at ${s.workspace}`];
  const head = `${s.kind} ingest: ${s.state} (phase=${s.phase})`;
  const passLine = s.passes.map((p) => `${p.name} ${p.unit ? unitStr(p.unit) : p.done ? "✓" : "-"}`).join("  ");
  const lines = [head, passLine];
  if (s.timings) lines.push(timingLine(s.timings));
  if (s.validation) lines.push(`validation: ${s.validation.ok ? "ok" : `${s.validation.errors} errors`}, ${s.validation.warnings} warnings`);
  if (s.has_skips) lines.push(`skipped episodes: ${s.skipped_episodes?.join(", ")}`);
  if (s.state === "failed") {
    for (const error of s.errors.slice(0, 3)) {
      lines.push(`${error.unit}: ${error.failure_type}/${error.failure_code} · ${error.retryable ? "retryable" : "terminal"}`);
    }
    if (s.failure_summary.retryable > 0) lines.push("rerun `scriptctl ingest` to resume retryable failures");
    if ((s.failure_summary.by_type.policy ?? 0) > 0) lines.push("provider policy blocked the source; rerunning the same source is unlikely to help");
    if ((s.failure_summary.by_type.authentication ?? 0) > 0 || (s.failure_summary.by_type.authorization ?? 0) > 0) {
      lines.push("check provider credentials and permissions before rerunning");
    }
  }
  return lines;
}

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m${String(s % 60).padStart(2, "0")}s` : `${s}s`;
}

function timingLine(t: TimingSummary): string {
  const parts = [`elapsed ${fmtDur(t.elapsedMs)}${t.done ? "" : " (running)"}`];
  const ser = t.serial;
  if (ser) {
    parts.push(`serial ${ser.doneWindows}/${ser.totalWindows} windows (${ser.doneEpisodes}/${ser.totalEpisodes} eps)`);
    if (ser.avgWindowMs != null) parts.push(`~${fmtDur(ser.avgWindowMs)}/window`);
    if (ser.etaMs != null && !t.done) parts.push(`eta ~${fmtDur(ser.etaMs)}`);
  }
  return parts.join(" · ");
}

export function commandIngestStatus(opts: Dict): [CommandOutput, number] {
  const workspacePath = strOf(opts["workspace_path"] || "workspace");
  const status = deriveIngestStatus(workspacePath);
  return [{ text: humanLines(status).join("\n"), data: { status } }, EXIT_OK];
}
